import { useEffect, useMemo } from 'react';
import { useForm, useWatch, type Control, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { ModelFormFields } from './model-form-fields';
import { canonicalInvoiceStatus } from '../lib/invoice-status';
import type { InvoiceRow } from '../types';
import {
  FINAL_SETTLEMENT_SUBMIT_FIELDS,
  type FinalSettlementSubmitValues,
} from './final-settlement-invoice-schema';
import { OverpaymentAcknowledge, PricingBasis, safeNumber } from './final-settlement-parts';
import { FinalInvoiceSellForm } from './final-invoice-sell-form';
import { PricingReadinessAlert } from './pricing-readiness-alert';
import { QpDialogWarning } from './qp-dialog-warning';
import {
  applyTradePrefill,
  buildFinalSettlementDefaults,
  type BlContextForTradeInvoice,
  type TicketContextForTradeInvoice,
  type TradePrefill,
} from './trade-invoice-payload';
import { useSuggestedInvoiceNumber } from '../api/use-suggested-invoice-number';
import { useAuthoritativeFinalPrefill } from './use-authoritative-final-prefill';
import { applyCoreValuesPrefill, useCoreValuesProvisionalPrefill } from './use-core-values-prefill';
import { useSuggestedNumberPrefill } from './use-suggested-number-prefill';

interface FormSource {
  readonly side: 'buy' | 'sell';
  readonly blOrderId: string;
  readonly ticket: TicketContextForTradeInvoice;
  readonly bl: BlContextForTradeInvoice;
  readonly allocatedDownpayment: number;
  readonly advanceOpen: number | null;
  readonly appliedProvisional: number;
  readonly existingInvoice: InvoiceRow | null;
  readonly prefill: TradePrefill | undefined;
}

// Return type left inferred: applyTradePrefill yields the schema's pre-parse
// (partial) shape, which is exactly what useForm's defaultValues expects.
function useFinalSettlementDefaults(src: FormSource, suggestedNumber: string | null) {
  const {
    side,
    ticket,
    bl,
    allocatedDownpayment,
    advanceOpen,
    appliedProvisional,
    existingInvoice,
    prefill,
  } = src;
  const authoritative = useAuthoritativeFinalPrefill(src.blOrderId, side);
  // The value log's current expectation beats the ticket/BL guess as the base
  // (only for fresh invoices); an AI extraction prefill still wins on top.
  const logPrefill = useCoreValuesProvisionalPrefill(src.blOrderId, side);
  return useMemo(() => {
    const base = buildFinalSettlementDefaults({
      side,
      ticket,
      bl,
      allocatedDownpayment,
      advanceOpen,
      appliedProvisional,
      existing: existingInvoice,
      suggestedNumber,
    });
    // A fresh invoice always prefills price/weight from the value log. An
    // existing sell-side Draft re-derives from the CURRENT log too, so a later
    // core-value correction (e.g. a fixed weight) flows into the amount instead
    // of staying frozen at its creation-time snapshot. Issued rows are
    // finalized legal documents and buy-side payables carry the supplier's own
    // billed figures, so both keep their stored values untouched.
    const rederiveFromLog =
      existingInvoice === null ||
      (side === 'sell' && canonicalInvoiceStatus(existingInvoice.status) === 'Draft');
    const withLog = rederiveFromLog
      ? applyCoreValuesPrefill(base, logPrefill, appliedProvisional)
      : base;
    const defaults = applyTradePrefill(withLog, prefill);
    // Once the value log holds a double-final side, final weight × fixed price
    // is authoritative and wins over the ticket-reference estimate (and any AI
    // prefill). Applies to a fresh Final and an editable sell-side Draft; an
    // issued row (or a buy-side edit) keeps its own figures.
    if (authoritative === null || !rederiveFromLog) return defaults;
    return applyCoreValuesPrefill(defaults, authoritative, appliedProvisional);
  }, [
    side,
    ticket,
    bl,
    allocatedDownpayment,
    advanceOpen,
    appliedProvisional,
    existingInvoice,
    prefill,
    suggestedNumber,
    authoritative,
    logPrefill,
  ]);
}

export function useFinalSettlementForm(
  open: boolean,
  src: FormSource,
): UseFormReturn<FinalSettlementSubmitValues> {
  const suggestionParams = {
    invoiceType: 'Final Settlement' as const,
    direction: src.side === 'buy' ? ('Purchase' as const) : ('Sale' as const),
    blOrderId: src.blOrderId,
    enabled: open && src.existingInvoice === null,
  };
  // The sell side hides the number field behind an auto-value, so the
  // suggestion must ride the defaults (the open-reset effect applies it);
  // the buy side's visible, operator-editable field goes through the
  // empty-only prefill hook below instead.
  const suggestion = useSuggestedInvoiceNumber(suggestionParams);
  const suggestedNumber = src.side === 'sell' ? (suggestion.data?.invoice_number ?? null) : null;
  const defaults = useFinalSettlementDefaults(src, suggestedNumber);
  const form = useForm<FinalSettlementSubmitValues>({
    resolver: zodResolver(FINAL_SETTLEMENT_SUBMIT_FIELDS),
    defaultValues: defaults,
  });

  useEffect(() => {
    if (open) form.reset(defaults);
  }, [open, defaults, form]);

  useSuggestedNumberPrefill(form, 'invoice_number', suggestionParams);

  return form;
}

export interface WatchedTotals {
  marketReferencePrice: number;
  amountQtMt: number;
  allocated: number;
  provisional: number;
  total: number;
  net: number;
  overpayment: boolean;
}

export function useWatchedTotals(control: Control<FinalSettlementSubmitValues>): WatchedTotals {
  const watched = useWatch({ control });
  const marketReferencePrice = safeNumber(watched.market_reference_price);
  const amountQtMt = safeNumber(watched.amount_qt_mt);
  const allocated = safeNumber(watched.applied_downpayment_amount);
  const provisional = safeNumber(watched.applied_provisional_amount);
  const total = safeNumber(watched.total_amount);
  const net = marketReferencePrice * amountQtMt - allocated - provisional;
  return {
    marketReferencePrice,
    amountQtMt,
    allocated,
    provisional,
    total,
    net,
    overpayment: net < 0,
  };
}

export interface FormBodyProps {
  readonly form: UseFormReturn<FinalSettlementSubmitValues>;
  readonly totals: WatchedTotals;
  readonly isPending: boolean;
  readonly side: 'buy' | 'sell';
  readonly blOrderId: string;
  readonly ticket: TicketContextForTradeInvoice;
  readonly bl: BlContextForTradeInvoice;
  readonly pricingReady: boolean;
  readonly missingFields: string[];
  readonly ticketId: string | null;
  readonly readinessLoaded: boolean;
  readonly acknowledged: boolean;
  readonly setAcknowledged: (next: boolean) => void;
  // The payment schedule is materialized into payment rows at creation and is
  // neither re-hydrated nor persisted on edit, so it's locked once the invoice
  // exists.
  readonly scheduleLocked: boolean;
}

export function FinalSettlementFormBody(props: FormBodyProps) {
  const { form, totals, isPending, ticket } = props;
  useAutoSyncTotalToNet(form, totals.net);
  return (
    <>
      {props.readinessLoaded && !props.pricingReady && (
        <PricingReadinessAlert
          missingFields={props.missingFields}
          ticketId={props.ticketId}
          testId="final-settlement-pricing-blocked"
        />
      )}
      <QpDialogWarning blOrderId={props.blOrderId} side={props.side} suppressWhenPriceFixed />
      {props.side === 'sell' ? (
        <FinalInvoiceSellForm
          form={form}
          totals={totals}
          blOrderId={props.blOrderId}
          currency={ticket.currency}
          counterpartyName={ticket.counterpartyName}
          bl={props.bl}
          disabled={isPending}
          scheduleLocked={props.scheduleLocked}
        />
      ) : (
        <>
          <PricingBasis ticket={ticket} />
          <ModelFormFields
            kind="Final_Settlement"
            side={props.side}
            register={form.register}
            control={form.control}
            errors={form.formState.errors}
            disabled={isPending}
            invoiceNumberPlaceholder="FINAL-001"
          />
        </>
      )}
      {totals.overpayment && (
        <OverpaymentAcknowledge
          checked={props.acknowledged}
          disabled={isPending}
          onChange={props.setAcknowledged}
        />
      )}
    </>
  );
}

// Keep Invoice total in step with the computed net while the user hasn't
// touched it themselves — otherwise the summary could show Net $9k and
// Invoice total $11k side by side, and Total is what actually saves.
// Once the operator types into Invoice total, react-hook-form marks it
// dirty and the sync stops, preserving manual overrides.
function useAutoSyncTotalToNet(
  form: UseFormReturn<FinalSettlementSubmitValues>,
  net: number,
): void {
  const isDirty = form.formState.dirtyFields.total_amount === true;
  useEffect(() => {
    if (isDirty) return;
    const next = Math.max(net, 0);
    if (form.getValues('total_amount') === next) return;
    form.setValue('total_amount', next, { shouldDirty: false });
  }, [form, isDirty, net]);
}
